import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ValidatorService } from 'angular-iban';
import * as ibantools from 'ibantools';
import { Observable, of, Subject } from 'rxjs';
import { map, share, take, takeUntil } from 'rxjs/operators';

import { ClientBankAccount, Invoice, InvoiceNotReadyError, InvoiceNotReadyViolation } from 'src/app/api/models';
import { InvoicesService } from 'src/app/api/services/invoices.service';
import { LeadSource } from 'src/app/model/lead-source';
import { Partner } from 'src/app/model/partner';
import { ClientPeerService } from 'src/app/service/client-peer.service';
import { ClientRepresentative } from '../../model/client-representative';
import { Invoice as LegacyInvoice } from '../../model/invoice';
import { ClientRepresentativesResource } from '../../model/model-resource';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { ValidateEmailList } from '../../validators/email-list.validator';

@Component({
  selector: 'app-invoice-info-widget',
  templateUrl: './invoice-info-widget.component.html',
  styleUrls: ['./invoice-info-widget.component.scss']
})
export class InvoiceInfoWidgetComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  form: UntypedFormGroup;
  offerForm: UntypedFormGroup;
  finalReportForm: UntypedFormGroup;

  invoice$: Observable<Invoice>;
  legacyInvoice$: Observable<LegacyInvoice>;
  maps$;

  expressPaymentChoices = [{
    value: true, label: 'Yes'
  }, {
    value: false, label: 'No'
  }];

  edited = false;
  locked = false;
  offerEdited = false;
  finalReportEdited = false;

  selectingLeadSource = false;
  selectedResponsibleSeller: Partner;
  selectingResponsibleSeller = false;
  selectedLeadSource: LeadSource;

  bankAccounts$: Observable<ClientBankAccount[]>;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private clientPeerService: ClientPeerService,
    private clientRepresentativeResource: ClientRepresentativesResource,
    private invoicesService: InvoicesService,
    private snackBar: MatSnackBar,
    private builder: UntypedFormBuilder
  ) {
    this.form = this.builder.group({
      amount: [null, Validators.min(0)],
      bankAccount: [null, ValidatorService.validateIban],
      dueDate: [null],
      expressPayment: [null],
      newInvoiceNumber: [null],
      newReferenceNumber: [null],
      number: [null],
      leadSourceId: [null],
      responsibleSellerId: [null],
    });
    this.offerForm = this.builder.group({
      recipients: [null, ValidateEmailList]
    });
    this.finalReportForm = this.builder.group({
      recipients: [null, ValidateEmailList]
    });
  }

  edit(invoice: LegacyInvoice, modernInvoice: Invoice): void {
    this.invoicePeerService.lock();
    this.edited = true;
    this.form.patchValue(invoice);
    this.form.patchValue({
      bankAccount: modernInvoice.iban,
    });
    this.selectedResponsibleSeller = invoice.responsibleSeller;
    this.selectedLeadSource = invoice.leadSource;
  }

  revert(invoice: LegacyInvoice): void {
    this.invoicePeerService.unlock();
    this.edited = false;
    this.form.reset();
    this.selectedResponsibleSeller = invoice.responsibleSeller;
    this.selectedLeadSource = invoice.leadSource;
  }

  save(i: LegacyInvoice): void {
    const invoice = i.clone();
    Object.assign(invoice, this.form.value);
    invoice.bankAccount = ibantools.electronicFormatIBAN(invoice.bankAccount);
    this.invoicePeerService
      .save(invoice)
      .subscribe(() => {
        this.edited = false;
      });
  }

  generateInvoiceNumber(invoice: LegacyInvoice): void {
    this.invoicePeerService.generateInvoiceNumber(invoice.invoiceId);
  }

  generateFinalReport(invoice: LegacyInvoice): void {
    this.getClientRepresentativeEmail(invoice).pipe(
      take(1),
    ).subscribe((email: string) => {
      this.invoicePeerService.lock();
      this.finalReportEdited = true;
      this.finalReportForm.patchValue({ recipients: email });
    });
  }

  cancelFinalReport(): void {
    this.invoicePeerService.unlock();
    this.finalReportEdited = false;
  }

  saveFinalReport(invoice: LegacyInvoice): void {
    this.invoicePeerService.unlock();
    this.finalReportEdited = false;
    this.invoicePeerService.generateFinalReport(invoice.invoiceId, this.finalReportForm.value.recipients);
  }

  /**
   * Opens the Generate Offer email-recipient form. Its submission goes
   * through cctool-go (POST /invoices/{id}/generate-offer), whose state
   * advance is guarded, as in the v2 Information widget.
   */
  generateOffer(invoice: LegacyInvoice): void {
    this.getClientRepresentativeEmail(invoice).pipe(
      take(1),
    ).subscribe((email: string) => {
      this.invoicePeerService.lock();
      this.offerEdited = true;
      this.offerForm.patchValue({ recipients: email });
    });
  }

  cancelOffer(): void {
    this.invoicePeerService.unlock();
    this.offerEdited = false;
  }

  saveOffer(invoice: LegacyInvoice): void {
    const recipientsRaw = this.offerForm.value.recipients ?? "";
    const recipients = recipientsRaw
      .split(",")
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0);

    this.invoicePeerService.unlock();
    this.offerEdited = false;

    // Workflow start/terminal toasts are produced globally by
    // WorkflowStatusToastHandler. The local error path only catches
    // pre-enqueue validation (422 invoice_not_ready), which is a
    // distinct surface the workflow_status events can't reach — the
    // workflow is never enqueued in that case.
    this.invoicesService.generateOffer({
      id: invoice.invoiceId,
      body: { recipients },
    }).subscribe({
      error: (err: HttpErrorResponse) => this.handleGenerateOfferError(err),
    });
  }

  private handleGenerateOfferError(err: HttpErrorResponse): void {
    if (err.status === 422 && this.isInvoiceNotReadyError(err.error)) {
      const lines = err.error.violations.map((v: InvoiceNotReadyViolation) => `• ${v.message}`);
      this.snackBar.open(
        `Cannot generate offer:\n${lines.join('\n')}`,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
    }
  }

  private isInvoiceNotReadyError(body: unknown): body is InvoiceNotReadyError {
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const candidate = body as Partial<InvoiceNotReadyError>;
    return candidate.error_code === 'invoice_not_ready_for_offer' && Array.isArray(candidate.violations);
  }

  sendInvoice(invoice: LegacyInvoice): void {
    this.invoicePeerService.sendInvoice(invoice.invoiceId);
  }

  sendModifiedOriginalInvoice(invoice: LegacyInvoice): void {
    this.invoicePeerService.sendModifiedOriginalInvoice(invoice.invoiceId);
  }

  sendToAccounting(invoice: LegacyInvoice): void {
    this.invoicePeerService.sendToAccounting(invoice.invoiceId);
  }

  sendToAccountingNew(invoice: Invoice): void {
    this.invoicesService.sendInvoiceToAccounting({ id: invoice.id }).subscribe();
  }

  selectLeadSource() {
    this.selectingLeadSource = true;
  }

  leadSourceSelected(leadSource?: LeadSource) {
    this.selectingLeadSource = false;
    this.selectedLeadSource = leadSource;
    this.form.patchValue({
      "leadSourceId": leadSource.leadSourceId,
    });
  }

  leadSourceCleared() {
    this.selectingLeadSource = false;
    this.selectedLeadSource = null;
    this.form.patchValue({
      "leadSourceId": null,
    });
  }

  selectResponsibleSeller() {
    this.selectingResponsibleSeller = true;
  }

  responsibleSellerSelected(responsibleSeller?: Partner) {
    this.selectingResponsibleSeller = false;
    this.selectedResponsibleSeller = responsibleSeller;
    this.form.patchValue({
      "responsibleSellerId": responsibleSeller.partnerId,
      "responsibleSeller": responsibleSeller
    });
  }

  responsibleSellerCleared() {
    this.selectingResponsibleSeller = false;
    this.selectedResponsibleSeller = null;
    this.form.patchValue({
      "responsibleSellerId": null,
    });
  }

  private getClientRepresentativeEmail(invoice: LegacyInvoice): Observable<string | null> {
    if (invoice.clientRepresentativeId !== null) {
      return this.clientRepresentativeResource.get(invoice.clientRepresentativeId)
        .pipe(
          map((clientRepresentative: ClientRepresentative) => clientRepresentative.email)
        );
    } else {
      return of(null);
    }
  }

  ngOnInit() {

    this.legacyInvoice$ = this.invoicePeerService.get();
    this.invoice$ = this.invoicePeerService.getInvoice();

    this.invoicePeerService.getState().subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });

    this.bankAccounts$ = this.clientPeerService.get()
      .pipe(
        takeUntil(this.ngDestroy$),
        map(client => client.bank_accounts),
        share()
      );
  }

  ngOnDestroy() {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }
}
